// src/support/leaderPages.ts
//
// Pure (framework-free) helpers for the config-driven Leader dashboard routing.
//
// The Leader dashboard's tab bar and per-(domain, mode) page routing are driven by `leader_page`
// rows (fetched via `useFetchLeaderPages` -> `db/leader-page`). This module turns those rows into
// the tab list and resolves the page def for the active (domain, mode), falling back to a built-in
// default map when the table is empty (pre-seed safety) so a fresh database still renders.
//
// The DB decides tab order/labels/enablement + which registered component (`component_key`) and
// ordered `regions` render. The `component_key -> React renderer` wiring lives with the dashboard
// (LeaderDashboardPage) because each page component needs domain-specific props/data. Adding a
// brand-new domain still needs a registered renderer; these rows make the existing routing editable.

import type { LeaderPage, LeaderPageMode } from '../types/dashboard';

export type LeaderDomain =
  | 'salesforce'
  | 'workshops'
  | 'pov'
  | 'campaigns'
  | 'enablement'
  | 'attainment'
  | 'firestarter';

export interface LeaderTab {
  domain: string;
  label: string;
  order: number;
  icon?: string | null;
  enabled: boolean;
}

// Built-in default page map. Mirrors `db/management/commands/seed_leader_pages.py` exactly so the
// hard-coded routing that shipped before the config-driven conversion is preserved until the seed
// lands. Kept in sync with the seed by hand (small, rarely changes).
export const DEFAULT_LEADER_PAGES: LeaderPage[] = [
  // Salesforce
  { id: -1, domain: 'salesforce', mode: 'team', label: 'Salesforce', order: 0,
    component_key: 'Leader.TeamSummaryPage',
    regions: ['dashboard', 'dashboard_secondary'], table_key: 'salesforce_team',
    enabled: true, last_updated: '' },
  { id: -2, domain: 'salesforce', mode: 'individual', label: 'Salesforce', order: 0,
    component_key: 'Leader.IndividualContributorPage',
    regions: ['scorecard'], table_key: 'salesforce_ic',
    enabled: true, last_updated: '' },

  // POV
  { id: -3, domain: 'pov', mode: 'team', label: 'POV', order: 1,
    component_key: 'Leader.PovTeamSummaryPage',
    regions: ['dashboard', 'dashboard_secondary'], table_key: 'pov_team',
    enabled: true, last_updated: '' },
  { id: -4, domain: 'pov', mode: 'individual', label: 'POV', order: 1,
    component_key: 'Leader.PovIndividualContributorPage',
    regions: [], table_key: 'pov_ic',
    enabled: true, last_updated: '' },

  // Workshops
  { id: -5, domain: 'workshops', mode: 'team', label: 'Workshops', order: 2,
    component_key: 'Leader.WorkshopsTeamSummaryPage',
    regions: ['dashboard'], table_key: 'workshops_team',
    enabled: true, last_updated: '' },
  { id: -6, domain: 'workshops', mode: 'individual', label: 'Workshops', order: 2,
    component_key: 'Leader.WorkshopsIndividualContributorPage',
    regions: ['overview'], table_key: 'workshops_ic',
    enabled: true, last_updated: '' },

  // Campaigns
  { id: -7, domain: 'campaigns', mode: 'team', label: 'Campaigns', order: 3,
    component_key: 'Leader.CampaignsTeamSummaryPage',
    regions: ['dashboard', 'dashboard_secondary'], table_key: 'campaigns_team',
    enabled: true, last_updated: '' },
  { id: -8, domain: 'campaigns', mode: 'individual', label: 'Campaigns', order: 3,
    component_key: 'Leader.CampaignsIndividualContributorPage',
    regions: ['dashboard'], table_key: 'campaigns_ic',
    enabled: true, last_updated: '' },

  // Enablement
  { id: -9, domain: 'enablement', mode: 'team', label: 'Enablement', order: 4,
    component_key: 'Leader.EnablementTeamSummaryPage',
    regions: ['education', 'journey'], table_key: 'enablement_team',
    enabled: true, last_updated: '' },
  { id: -10, domain: 'enablement', mode: 'individual', label: 'Enablement', order: 4,
    component_key: 'Leader.EnablementIndividualContributorPage',
    regions: ['education', 'journey'], table_key: 'enablement_ic',
    enabled: true, last_updated: '' },
];

// The Attainment tab is front-end driven (its reporting structure comes from the goal/attainment drill
// API, not the DB-configured org), so it is appended whenever the fetched rows don't define it.
export const ATTAINMENT_DOMAIN = 'attainment';
const ATTAINMENT_LEADER_PAGES: LeaderPage[] = [
  { id: -11, domain: ATTAINMENT_DOMAIN, mode: 'team', label: 'Attainment', order: 5,
    component_key: 'Leader.AttainmentTeamSummaryPage',
    regions: [], table_key: null,
    enabled: true, last_updated: '' },
  { id: -12, domain: ATTAINMENT_DOMAIN, mode: 'individual', label: 'Attainment', order: 5,
    component_key: 'Leader.AttainmentIndividualContributorPage',
    regions: [], table_key: null,
    enabled: true, last_updated: '' },
];

/**
 * Domains whose tab is switched off for now. Their `leader_page` rows, placements and tiles stay in the database
 * untouched, so removing a domain from this set brings the tab back as it was.
 */
export const HIDDEN_LEADER_DOMAINS: ReadonlySet<string> = new Set(['firestarter']);

/**
 * The effective page rows. The fetched rows decide for every domain they mention (so a row can relabel,
 * reorder or disable a tab: set `enabled` false to hide one). Any built-in default domain the rows do not
 * mention at all is kept, so a table that holds only SOME domains (e.g. just a newly added tab) can never
 * hide the built-in ones; with no rows at all, the defaults are used as before. Attainment is front-end
 * driven and appended when the rows do not define it.
 */
export function resolveLeaderPages(
  pages: LeaderPage[] | undefined | null,
  hiddenDomains: ReadonlySet<string> = HIDDEN_LEADER_DOMAINS,
): LeaderPage[] {
  const rows = (Array.isArray(pages) ? pages : []).filter((p) => !hiddenDomains.has(p.domain));
  const defined = new Set(rows.map((p) => p.domain));
  const base = [...rows, ...DEFAULT_LEADER_PAGES.filter((p) => !defined.has(p.domain) && !hiddenDomains.has(p.domain))];
  return base.some((p) => p.domain === ATTAINMENT_DOMAIN) ? base : [...base, ...ATTAINMENT_LEADER_PAGES];
}

/**
 * The domain tab list, one per distinct domain, ordered by `order` then first-seen. Tab metadata
 * (label/icon/order) is taken from the domain's lowest-order row; a domain is `enabled` if any of
 * its (domain, mode) rows is enabled.
 */
export function deriveLeaderTabs(pages: LeaderPage[]): LeaderTab[] {
  const byDomain = new Map<string, LeaderTab>();
  const sorted = pages
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id - b.id);

  for (const p of sorted) {
    const existing = byDomain.get(p.domain);
    if (!existing) {
      byDomain.set(p.domain, {
        domain: p.domain,
        label: p.label,
        order: p.order ?? 0,
        icon: p.icon ?? null,
        enabled: !!p.enabled,
      });
    } else if (p.enabled) {
      existing.enabled = true;
    }
  }

  return Array.from(byDomain.values())
    .filter((t) => t.enabled)
    .sort((a, b) => a.order - b.order);
}

/** The page def for a given (domain, mode), or null when none is configured/enabled. */
export function findLeaderPage(
  pages: LeaderPage[],
  domain: string,
  mode: LeaderPageMode,
): LeaderPage | null {
  return (
    pages.find((p) => p.domain === domain && p.mode === mode && p.enabled) ?? null
  );
}

/** The first domain to show on load (lowest-order enabled tab), defaulting to `salesforce`. */
export function defaultLeaderDomain(tabs: LeaderTab[]): string {
  return tabs[0]?.domain ?? 'salesforce';
}
